import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FloatingFruits } from "../components/FloatingFruits";
import { ProductCard } from "../components/ProductCard";
import { ProductPopup } from "../components/ProductPopup";
import { OffersSection } from "../components/OffersSection";
import { useProducts } from "../hooks/useProducts";
import type { Product } from "../types";

const papoJujubesImg = "/images/products/papo-jujubes.jpg";
const fruitPatternImg = "/images/fruit-pattern.jpg";

// Marquee Component
const Marquee = () => (
  <div className="bg-sansi-orange text-black py-3 overflow-hidden whitespace-nowrap border-y-4 border-black relative z-20 rotate-1">
    <div className="animate-marquee inline-block font-black text-2xl uppercase tracking-widest">
      No Added Sugar &nbsp;✦&nbsp; 100% Natural &nbsp;✦&nbsp; Vegan Friendly
      &nbsp;✦&nbsp; Made in Sri Lanka &nbsp;✦&nbsp; No Added Sugar &nbsp;✦&nbsp;
      100% Natural &nbsp;✦&nbsp; Vegan Friendly &nbsp;✦&nbsp; Made in Sri Lanka
      &nbsp;✦&nbsp; No Added Sugar &nbsp;✦&nbsp; 100% Natural &nbsp;✦&nbsp;
      Vegan Friendly &nbsp;✦&nbsp; Made in Sri Lanka &nbsp;✦&nbsp; No Added
      Sugar &nbsp;✦&nbsp; 100% Natural &nbsp;✦&nbsp; Vegan Friendly
      &nbsp;✦&nbsp; Made in Sri Lanka &nbsp;✦&nbsp;
    </div>
  </div>
);

interface HomeProps {
  onAddToCart: (product: Product, quantity?: number) => void;
}

export function Home({ onAddToCart }: HomeProps) {
  const [scrollY, setScrollY] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Load products from CSV
  const { regularProducts, isLoading } = useProducts();

  // Pagination constants
  const ITEMS_PER_PAGE = 12;
  const totalPages = Math.ceil(regularProducts.length / ITEMS_PER_PAGE);

  // Calculate paginated products
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = regularProducts.slice(startIndex, endIndex);

  // Reset to page 1 when products change
  useEffect(() => {
    setCurrentPage(1);
  }, [regularProducts.length]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="grow">
      {/* Bold Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col justify-center items-center text-center px-4 pt-20 md:pt-28">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 blur-sm"
          style={{
            backgroundImage: `url(${papoJujubesImg})`,
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        />
        <div className="absolute inset-0 bg-linear-to-b from-black/80 via-black/50 to-sansi-black" />

        {/* Fruit Pattern Overlay */}
        <div
          className="fruit-pattern-bg"
          style={{ backgroundImage: `url(${fruitPatternImg})` }}
        />

        <FloatingFruits />

        <div className="relative z-10 max-w-5xl mx-auto space-y-6">
          <span className="inline-block px-4 py-2 border-2 border-sansi-green text-sansi-green font-mono text-sm uppercase tracking-widest hover:bg-sansi-green hover:text-black transition-colors cursor-default">
            Premium Dehydrated Fruits
          </span>

          <h1 className="font-display text-5xl md:text-9xl uppercase leading-[0.9] text-white mix-blend-difference">
            Taste <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
              Nature
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 font-light max-w-2xl mx-auto">
            Experience the{" "}
            <span className="brush-underline brush-orange">true flavor</span> of
            Sri Lankan fruits. <br className="hidden md:block" />
            Preserved{" "}
            <span className="brush-underline brush-green">naturally</span>, just
            for you.
          </p>

          <div className="pt-8">
            <button
              onClick={() =>
                document
                  .getElementById("products")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="group relative inline-flex items-center justify-center px-8 py-4 bg-sansi-green text-black font-black uppercase tracking-wider text-lg hover:bg-white transition-colors duration-300 transform hover:-translate-y-1 hover:shadow-[4px_4px_0px_var(--color-sansi-orange)]"
            >
              Shop Now
            </button>
          </div>
        </div>
      </section>

      <Marquee />

      <OffersSection
        onAddToCart={onAddToCart}
        onProductClick={(product: Product) => setSelectedProduct(product)}
      />

      {/* Product Grid */}
      <section id="products" className="py-24 px-6 container mx-auto relative">
        <div className="flex items-end justify-between mb-16 border-b border-white/20 pb-6">
          <h2 className="font-display text-5xl md:text-6xl uppercase">
            Our <span className="text-sansi-green">Picks</span>
          </h2>
          <p className="hidden md:block text-gray-400 font-mono">EST. 2023</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-8 md:gap-x-8 md:gap-y-16">
          {isLoading ? (
            <div className="col-span-full text-center text-gray-400 py-12">
              Loading products...
            </div>
          ) : (
            paginatedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
                onClick={() => setSelectedProduct(product)}
              />
            ))
          )}
        </div>

        {/* Pagination Navigation */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-16 pt-8 border-t border-white/20">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="p-3 border border-white/20 hover:bg-sansi-orange hover:text-black hover:border-sansi-orange transition-colors rounded-full text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-white disabled:hover:border-white/20"
              aria-label="Previous page"
            >
              <ArrowLeft size={24} />
            </button>

            <span className="text-white font-mono text-lg px-4">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="p-3 border border-white/20 hover:bg-sansi-orange hover:text-black hover:border-sansi-orange transition-colors rounded-full text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-white disabled:hover:border-white/20"
              aria-label="Next page"
            >
              <ArrowRight size={24} />
            </button>
          </div>
        )}
      </section>

      <ProductPopup
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={onAddToCart}
      />
    </div>
  );
}
